import { Box, Tabs, Button, styled } from '@mui/material';
import { Typography } from '../../components/atoms';

interface StyledTabsProps {
    isPlanDetailsEnhancementEnabled?: boolean;
    ftds?: boolean;
}

interface StyledMenuItemProps {
    isFirst?: boolean;
    isLast?: boolean;
    singleItem?: boolean;
}

export const TabsWrapper = styled(Box)({
    width: '100%'
});
export const MenuWrapper = styled(Box)({
    width: '31.85%',
    // Plan Overview part 2 (AEX-3498): keep the goal-list sidebar pinned while the plan content
    // scrolls, with its own internal scroll for long lists. `alignSelf: flex-start` stops the flex
    // item from stretching to the row height so sticky can engage. Top offset clears the fixed
    // global nav (60px) + the pinned "Plan Overview" heading (StickyRegion top:60, ~90px tall).
    position: 'sticky',
    top: '150px',
    alignSelf: 'flex-start',
    maxHeight: 'calc(100vh - 166px)',
    display: 'flex',
    flexDirection: 'column'
});
export const StyledTabs = styled(Tabs, {
    shouldForwardProp: (prop) => prop !== 'isPlanDetailsEnhancementEnabled' && prop !== 'ftds'
})<StyledTabsProps>(({ theme, isPlanDetailsEnhancementEnabled, ftds }) => {
    if (ftds) {
        const brandColor = theme.palette.semantic.int.selected;
        return {
            width: '100%',
            border: 'none',
            borderRadius: 0,
            backgroundColor: 'transparent',
            minHeight: '54px',
            overflow: 'visible',
            '& .MuiTabs-scroller': {
                overflow: 'visible !important'
            },
            '& .MuiTabs-flexContainer': {
                height: '54px',
                alignItems: 'flex-end',
                gap: '4px',
                borderBottom: `1px solid ${brandColor}`
            },
            '& .MuiTabs-indicator': {
                display: 'none'
            },
            '& .MuiTab-root': {
                flex: '0 0 auto',
                maxWidth: 'none',
                minWidth: 0,
                minHeight: '54px',
                boxSizing: 'border-box',
                textTransform: 'none',
                fontFamily: '"TT Commons Pro"',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '22px',
                color: theme.palette.neutral[700],
                padding: '16px 32px',
                marginBottom: '-1px',
                border: `1px solid ${theme.palette.neutral[200]}`,
                borderBottom: 'none',
                borderTopLeftRadius: '4px',
                borderTopRightRadius: '4px',
                backgroundColor: 'transparent',
                '&.Mui-selected': {
                    color: theme.palette.neutral[950],
                    fontWeight: 700,
                    backgroundColor: theme.palette.neutral.white,
                    borderColor: brandColor,
                    borderTop: `6px solid ${brandColor}`,
                    borderBottom: 'none'
                }
            }
        };
    }
    return {
        width: '100%',
        borderRadius: isPlanDetailsEnhancementEnabled ? '0 16px 0 0' : '16px 16px 0 0',
        border: `1px solid ${theme.palette.grey[100]}`,
        backgroundColor: theme.palette.neutral.white,
        minHeight: '48px',
        '& .MuiTabs-flexContainer': {
            height: '100%'
        },
        '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.primary[600],
            height: '3px',
            borderRadius: '2px 2px 0 0'
        },
        '& .MuiTab-root': {
            maxWidth: '100%',
            flex: 1,
            position: 'relative',
            textTransform: 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontFamily: '"TT Commons Pro"',
            fontSize: 'var(--font-font-size-125-rem, 20px)',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '24px',
            color: theme.palette.neutral.black,
            minHeight: '48px',
            padding: '12px 16px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '8px',
            '&.Mui-selected': {
                color: theme.palette.primary[500],
                fontWeight: 600
            },
            '&:not(:last-child)::after': {
                content: '""',
                position: 'absolute',
                right: '0',
                top: '50%',
                transform: 'translateY(-50%)',
                height: '24px',
                width: '1px',
                backgroundColor: theme.palette.grey[200]
            }
        }
    };
});

export const SecondaryTabsWrapper = styled(Box)({
    width: '100%',
    paddingLeft: '20px',
    paddingRight: '14px',
    paddingTop: '8px',
    paddingBottom: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
});

export const SecondaryStyledTabs = styled(Tabs)(({ theme }) => ({
    width: 'fit-content',
    backgroundColor: theme.palette.semantic.bg.subtle,
    borderRadius: '100px',
    padding: '4px',
    minHeight: '40px',
    '& .MuiTabs-flexContainer': {
        height: '100%',
        gap: '4px'
    },
    '& .MuiTabs-indicator': {
        display: 'none'
    },
    '& .MuiTab-root': {
        textTransform: 'none',
        borderRadius: '100px',
        minHeight: '32px',
        padding: '8px 16px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontVariantNumeric: 'lining-nums tabular-nums',
        fontFamily: '"TT Commons Pro"',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '20px',
        color: theme.palette.semantic.text.default,
        transition: 'all 0.2s ease',
        '&.Mui-selected': {
            backgroundColor: theme.palette.semantic.bg.default,
            color: theme.palette.semantic.text.default,
            fontWeight: 600
        },
        '&:hover': {
            backgroundColor: theme.palette.semantic.bg.default
        }
    }
}));

export const EditPlanButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    borderRadius: '100px',
    padding: '10px 24px',
    fontFamily: '"TT Commons Pro"',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '24px',
    color: theme.palette.semantic.int.link,
    border: `1px solid ${theme.palette.semantic.bd.brand}`,
    backgroundColor: 'transparent',
    '&:hover': {
        backgroundColor: 'rgba(55, 105, 255, 0.04)',
        border: `1px solid ${theme.palette.semantic.bd.brand}`
    }
}));

export const EditPlanContainer = styled(Box)({
    width: '100%',
    padding: '0 40px 40px 40px',
    display: 'flex',
    flexDirection: 'column'
});

export const EditPlanActions = styled(Box)({
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px'
});

export const ExitEditButton = styled(Button)(({ theme }) => ({
    minWidth: '44px',
    width: '44px',
    height: '44px',
    borderRadius: '4px',
    padding: 0,
    border: `1px solid ${theme.palette.primary[600]}`,
    color: theme.palette.primary[600],
    backgroundColor: theme.palette.neutral.white,
    boxShadow: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
        border: `1px solid ${theme.palette.primary[600]}`,
        backgroundColor: 'rgba(47, 111, 237, 0.04)'
    }
}));

export const EditPlanColumnsHeader = styled(Box)({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'center',
    borderRadius: '0px'
});

export const EditColumnTitle = styled(Typography)(({ theme }) => ({
    fontFamily: '"TT Commons Pro"',
    fontSize: '20px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '24px',
    color: theme.palette.semantic.text.default,
    border: `1px solid ${theme.palette.semantic.bd.default}`,
    background: theme.palette.semantic.bg.subtle,
    padding: '16px 70px',
    borderRadius: '0px',
    marginLeft: '-40px',
    marginRight: '-40px'
}));

export const PlanAttentionBadge = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: '"TT Commons Pro"',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '18px',
    color: theme.palette.berry[400]
}));

export const PlanAttentionDot = styled('span')(({ theme }) => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: theme.palette.berry[400]
}));

export const EditComparisonWrapper = styled(Box)({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '0'
});

export const EditComparisonRow = styled(Box)({
    display: 'grid',
    gridTemplateColumns: '200px 1fr 1fr',
    columnGap: '48px',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '80px',
    borderBottom: '1px solid #F0F0F0',
    '&:last-of-type': {
        borderBottom: 'none'
    }
});

export const EditRowLabel = styled(Typography)(({ theme }) => ({
    display: 'flex',
    alignItems: 'right',
    gap: '8px',
    fontFamily: '"TT Commons Pro"',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 500,
    maxWidth: '160px',
    lineHeight: '20px',
    color: theme.palette.semantic.text.default
}));

export const EditComparisonCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.semantic.bg.default,
    // borderRadius: '20px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '12px',
    minHeight: '120px',
    '& .highcharts-container': {
        width: '100% !important',
        height: 'auto !important'
    }
}));

export const EditComparisonCardLarge = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.semantic.bg.default,
    // borderRadius: '20px',
    padding: '32px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'justify',
    alignItems: 'justify',
    gap: '16px',
    minHeight: '280px',
    '& .highcharts-container': {
        width: '100% !important',
        height: 'auto !important'
    }
}));

export const EditComparisonCardAdjusted = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.semantic.bg.default,
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    // gap: '12px',
    minHeight: '120px',
    '& .highcharts-container': {
        width: '100% !important',
        height: 'auto !important'
    }
}));

export const EditComparisonCardLargeAdjusted = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.semantic.bg.default,
    padding: '32px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minHeight: '280px',
    '& .highcharts-container': {
        width: '100% !important',
        height: 'auto !important'
    }
}));

export const EditMetricValue = styled(Typography)(({ theme }) => ({
    justifyContent: 'center',
    alignItems: 'center',
    fontFamily: '"TT Commons Pro"',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '20px',
    color: theme.palette.semantic.text.default,
    fontVariantNumeric: 'lining-nums tabular-nums'
}));

export const EditMetricSubtext = styled(Typography)(({ theme }) => ({
    fontFamily: '"TT Commons Pro"',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '20px',
    color: theme.palette.semantic.text.muted
}));

export const EditMetricStack = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '32px',
        bottom: '0',
        left: '14%',
        maxHeight: 'calc(100% - 32px)',
        transform: 'translateX(-0.5px)',
        borderLeft: '1px solid #D9D9D9'
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        top: '32px',
        bottom: '0',
        left: '50%',
        transform: 'translateX(-0.5px)',
        borderLeft: '1px solid #D9D9D9'
    }
});

export const ContentWrapper = styled(Box)(
    ({ isPlanDetailsEnhancementEnabled }: { isPlanDetailsEnhancementEnabled?: boolean }) => ({
        width: '100%',
        padding: isPlanDetailsEnhancementEnabled ? '0 48px 42px 8px' : '0 48px 42px 52px',
        display: 'grid',
        gridTemplateColumns: '1.6fr 1fr 1fr',
        gap: '24px'
    })
);

export const Column = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    '&:not(:last-child)': {
        borderRight: '1px solid #D9D9D9',
        paddingRight: '24px'
    },
    '&:not(:first-of-type)': {
        paddingLeft: '24px'
    }
});

export const SectionTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.semantic.text.default,
    fontFamily: '"TT Commons Pro"',
    fontSize: 'var(--font-font-size-125-rem, 20px)',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '24px'
}));

export const LabelLarge = styled(Typography)(({ theme }) => ({
    color: theme.palette.semantic.text.muted,
    fontVariantNumeric: 'lining-nums tabular-nums',
    fontFamily: '"TT Commons Pro"',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '20px'
}));

export const StyledChipContainer = styled(Box)({
    width: '70px',
    height: '26px',
    flexShrink: 0,
    borderRadius: '100px'
});

export const ValueText = styled(Typography)(({ theme }) => ({
    color: theme.palette.semantic.text.default,
    fontVariantNumeric: 'lining-nums tabular-nums',
    fontFamily: '"TT Commons Pro"',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '28px',
    marginTop: '8px'
}));

export const SectionBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '24px',
    paddingBottom: '24px',
    borderBottom: `1px solid ${theme.palette.semantic.bd.default}`,
    '&:first-of-type': {
        paddingTop: 0
    },
    '&:last-of-type': {
        borderBottom: 'none',
        paddingBottom: 0
    }
}));

export const SectionHeader = styled(Typography)(({ theme }) => ({
    color: theme.palette.semantic.text.default,
    fontFamily: '"TT Commons Pro"',
    fontSize: 'var(--font-font-size-125-rem, 20px)',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '24px',
    paddingBottom: '16px',
    borderBottom: `1px solid ${theme.palette.semantic.bd.default}`,
    marginBottom: '24px'
}));

export const StyledSingleSelectMenuParent = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    // minHeight:0 lets this flex child shrink below its content height so the goal list can
    // scroll internally (own scroll) within the sticky sidebar.
    minHeight: 0,
    overflowY: 'auto',
    // FTDS: the goal list sits directly in the sidebar with no surrounding card/border.
    // Thin FTDS scrollbar for the goal list (matches the sidebar scroll handle in Figma).
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.neutral[400]} transparent`,
    '&::-webkit-scrollbar': {
        width: '6px',
        backgroundColor: 'transparent'
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.neutral[400],
        borderRadius: '100px'
    }
}));

export const StyledMenuItem = styled('div')<StyledMenuItemProps>(({ theme }) => ({
    // FTDS: each goal row reads as an underlined text link (brand colour, 14px) inside the sidebar.
    padding: '8px',
    minHeight: '40px',
    boxSizing: 'border-box',
    cursor: 'pointer',
    borderRadius: '8px',
    fontFamily: '"TT Commons Pro"',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '19px',
    color: theme.palette.semantic.text.brand,
    textDecorationLine: 'underline',
    textUnderlinePosition: 'from-font',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    '&.selected': {
        backgroundColor: theme.palette.semantic.bg.brandLight1,
        fontWeight: 700
    },
    // Flat goal list (single-portfolio tenants): link blue rows, selected row on the light brand fill with dark brand text.
    '&.flat': {
        color: theme.palette.semantic.int.link
    },
    '&.flat.selected': {
        backgroundColor: theme.palette.semantic.bg.brandLight2,
        color: theme.palette.semantic.bg.brandDark,
        fontWeight: 700
    },
    '&.disabled': {
        color: theme.palette.semantic.text.mutedLight,
        textDecorationLine: 'none',
        cursor: 'default',
        fontVariantNumeric: 'lining-nums tabular-nums',
        '> svg': {
            width: '17px',
            height: '17px'
        }
    }
}));

export const StyledCategory = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    // FTDS: term groups (Short/Medium/Long) are separated by a divider rule; the 4px above/below
    // matches the Figma `_link--table` gap around the divider.
    paddingBottom: '4px',
    marginBottom: '4px',
    borderBottom: `1px solid ${theme.palette.semantic.bd.default}`,
    '&:last-of-type': {
        paddingBottom: 0,
        marginBottom: 0,
        borderBottom: 'none'
    },
    '& .category-label': {
        display: 'flex',
        minHeight: '40px',
        boxSizing: 'border-box',
        padding: '8px',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        alignSelf: 'stretch',
        color: theme.palette.semantic.text.default,
        fontFamily: '"TT Commons Pro"',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: '19px',
        '&.disabled': {
            color: theme.palette.semantic.text.mutedLight,
            fontVariantNumeric: 'lining-nums tabular-nums',
            fontFamily: '"TT Commons Pro"',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: '19px' /* 135% */
        }
    }
}));
